import React, { useState, useEffect, useCallback } from 'react'
import {
  Card,
  Typography,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Select,
  DatePicker,
  Switch,
  Input,
  InputNumber,
  Steps,
  Checkbox,
  Descriptions,
  Divider,
  Popconfirm,
  Result,
  App
} from 'antd'
import dayjs from 'dayjs'
import api from '../../utils/api'
import { PLAN_SHORT_NAME } from '../../constants/plans'
import Hint from '../../components/common/Hint'
import { now as tzNow, fromPicker } from '../../utils/timezone'

const { Title, Text } = Typography
const { TextArea } = Input

// 方案名稱統一由 src/constants/plans.js 提供
// 注意：business（商務方案）已停售，僅既有店家保留，不應再新指派
const PLAN_LABEL = PLAN_SHORT_NAME

const STATUS_LABEL = {
  active: '有效',
  expired: '已過期',
  cancelled: '已取消'
}

const ActiveStoresAdmin = () => {
  const { message } = App.useApp()
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  })
  const [filters, setFilters] = useState({
    search: '',
    subscriptionStatus: 'active',
    plan: 'all',
    storeEnabled: 'all'
  })
  const [editOpen, setEditOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)

  // 金流綁定
  const [payOpen, setPayOpen] = useState(false)
  const [payTarget, setPayTarget] = useState(null)
  const [payForm] = Form.useForm()
  const [paySaving, setPaySaving] = useState(false)

  const openPaymentConfig = (record) => {
    setPayTarget(record)
    payForm.resetFields()
    setPayOpen(true)
  }

  const submitPaymentConfig = async (values) => {
    if (!payTarget?._id) return
    setPaySaving(true)
    try {
      await api.post(`/api/admin/businesses/${payTarget._id}/payment-config`, {
        MerchantID: values.MerchantID?.trim(),
        HashKey: values.HashKey?.trim(),
        HashIV: values.HashIV?.trim()
      })
      message.success('金流綁定成功')
      setPayOpen(false)
      setPayTarget(null)
      fetchData()
    } catch (e) {
      message.error(
        e?.response?.data?.message || '綁定失敗，請確認金鑰是否正確'
      )
    } finally {
      setPaySaving(false)
    }
  }

  const fetchData = useCallback(
    async (page = 1, pageSize = 20) => {
      setLoading(true)
      try {
        const params = {
          page,
          limit: pageSize,
          search: filters.search.trim() || undefined,
          subscriptionStatus:
            filters.subscriptionStatus === 'all'
              ? undefined
              : filters.subscriptionStatus,
          plan: filters.plan === 'all' ? undefined : filters.plan,
          storeEnabled:
            filters.storeEnabled === 'all' ? undefined : filters.storeEnabled
        }
        const res = await api.get('/api/admin/businesses', { params })
        if (res.data?.success) {
          setData(res.data.data || [])
          const p = res.data.pagination || {}
          setPagination({
            current: p.currentPage || page,
            pageSize: p.itemsPerPage || pageSize,
            total: p.totalItems || 0
          })
        } else {
          message.error(res.data?.message || '載入失敗')
        }
      } catch (e) {
        message.error(e.response?.data?.message || '載入店家列表失敗')
      } finally {
        setLoading(false)
      }
    },
    [filters, message]
  )

  useEffect(() => {
    fetchData(1, pagination.pageSize)
  }, [])

  const onSearch = () => {
    fetchData(1, pagination.pageSize)
  }

  const openEdit = (record) => {
    setEditing(record)
    const sub = record.subscription || {}
    form.setFieldsValue({
      plan: sub.plan || 'free',
      status: sub.status || 'active',
      startDate: sub.startDate ? dayjs(sub.startDate) : null,
      endDate: sub.endDate ? dayjs(sub.endDate) : null,
      duration: sub.duration,
      autoRenew: !!sub.autoRenew,
      isContracted: sub.isContracted !== false,
      contractNotes: sub.contractNotes || '',
      storeEnabled: !!record.storeEnabled
    })
    setEditOpen(true)
  }

  const handleSave = async () => {
    try {
      const v = await form.validateFields()
      setSaving(true)
      const payload = {
        plan: v.plan,
        status: v.status,
        // fromPicker：訂閱起訖日照使用者挑的那一天送，不要被裝置時區往前推
        startDate: v.startDate ? fromPicker(v.startDate).toISOString() : null,
        endDate: v.endDate ? fromPicker(v.endDate).toISOString() : null,
        duration: v.duration,
        autoRenew: v.autoRenew,
        isContracted: v.isContracted,
        contractNotes: v.contractNotes || undefined,
        storeEnabled: v.storeEnabled
      }
      await api.put(
        `/api/admin/businesses/${editing._id}/subscription`,
        payload
      )
      message.success('已更新')
      setEditOpen(false)
      setEditing(null)
      fetchData(pagination.current, pagination.pageSize)
    } catch (e) {
      if (e?.errorFields) return
      message.error(e.response?.data?.message || '更新失敗')
    } finally {
      setSaving(false)
    }
  }

  // ── 退訂／終止服務精靈 ──────────────────────────────
  const [termOpen, setTermOpen] = useState(false)
  const [termTarget, setTermTarget] = useState(null)
  const [termInfo, setTermInfo] = useState(null)
  const [termInfoLoading, setTermInfoLoading] = useState(false)
  const [termStep, setTermStep] = useState(0)
  const [termRunning, setTermRunning] = useState(false)
  const [termResult, setTermResult] = useState(null)
  const [termForm] = Form.useForm()

  const openTerminate = async (record) => {
    setTermTarget(record)
    setTermOpen(true)
    setTermStep(0)
    setTermResult(null)
    setTermInfo(null)
    termForm.setFieldsValue({
      effectiveDate: tzNow(),
      reason: '',
      removeLine: true,
      cancelSubscription: true
    })
    setTermInfoLoading(true)
    try {
      const res = await api.get(`/api/admin/businesses/${record._id}/termination`)
      if (res.data?.success) setTermInfo(res.data.data)
    } catch (e) {
      message.error(e.response?.data?.message || '載入退訂資訊失敗')
    } finally {
      setTermInfoLoading(false)
    }
  }

  const downloadCustomers = async (businessId, name) => {
    try {
      const res = await api.get(
        `/api/admin/businesses/${businessId}/customers/export`,
        { responseType: 'blob' }
      )
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a')
      a.href = url
      const safe = (name || businessId).replace(/[\\/:*?"<>|]/g, '_').slice(0, 40)
      a.download = `customers_${safe}_${tzNow().format('YYYY-MM-DD')}.xlsx`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      message.success('顧客資料 Excel 已開始下載')
    } catch (e) {
      message.error('匯出顧客資料失敗')
    }
  }

  const doTerminate = async () => {
    try {
      const v = await termForm.validateFields()
      setTermRunning(true)
      const payload = {
        effectiveDate: v.effectiveDate
          ? fromPicker(v.effectiveDate).toISOString()
          : undefined,
        reason: v.reason || '',
        removeLine: !!v.removeLine,
        cancelSubscription: !!v.cancelSubscription
      }
      const res = await api.post(
        `/api/admin/businesses/${termTarget._id}/terminate`,
        payload
      )
      if (res.data?.success) {
        setTermResult(res.data.data)
        setTermStep(2)
        message.success('退訂處理完成')
        fetchData(pagination.current, pagination.pageSize)
      } else {
        message.error(res.data?.message || '退訂處理失敗')
      }
    } catch (e) {
      if (e?.errorFields) return
      message.error(e.response?.data?.message || '退訂處理失敗')
    } finally {
      setTermRunning(false)
    }
  }

  const doReactivate = async (record) => {
    try {
      await api.post(`/api/admin/businesses/${record._id}/reactivate`, {})
      message.success('已復原，重新開放線上預約')
      fetchData(pagination.current, pagination.pageSize)
    } catch (e) {
      message.error(e.response?.data?.message || '復原失敗')
    }
  }

  const columns = [
    {
      title: '店家名稱',
      dataIndex: 'name',
      key: 'name',
      render: (t, r) => t || r.username || '—'
    },
    { title: '帳號', dataIndex: 'username', key: 'username' },
    { title: '電話', dataIndex: 'phone', key: 'phone' },
    {
      title: '訂閱方案',
      key: 'plan',
      render: (_, r) => {
        const p = r.subscription?.plan || 'free'
        return <Tag color='blue'>{PLAN_LABEL[p] || p}</Tag>
      }
    },
    {
      title: '訂閱狀態',
      key: 'status',
      render: (_, r) => {
        const s = r.subscription?.status || 'active'
        const color =
          s === 'active' ? 'green' : s === 'expired' ? 'orange' : 'default'
        return <Tag color={color}>{STATUS_LABEL[s] || s}</Tag>
      }
    },
    {
      title: '到期日',
      key: 'endDate',
      render: (_, r) => {
        const d = r.subscription?.endDate
        if (!d) return '—'
        const str = dayjs(d).format('YYYY-MM-DD')
        if (r.pastEndDate) {
          return (
            <Space>
              <Text type='danger'>{str}</Text>
              <Tag color='red'>已過期</Tag>
            </Space>
          )
        }
        return str
      }
    },
    {
      title: '店家啟用',
      dataIndex: 'storeEnabled',
      key: 'storeEnabled',
      render: (v) =>
        v ? <Tag color='success'>啟用</Tag> : <Tag>關閉</Tag>
    },
    {
      title: '服務狀態',
      key: 'termination',
      render: (_, r) => {
        const t = r.serviceTermination
        if (!t || !t.isTerminated) return <Tag color='green'>服務中</Tag>
        const eff = t.effectiveDate ? dayjs(t.effectiveDate) : null
        const pending = eff && eff.isAfter(tzNow())
        return (
          <Space direction='vertical' size={0}>
            <Tag color={pending ? 'orange' : 'red'}>
              {pending ? '已排定退訂' : '已退訂'}
            </Tag>
            {eff && (
              <Text type='secondary' style={{ fontSize: 12 }}>
                停約生效：{eff.format('YYYY-MM-DD')}
              </Text>
            )}
          </Space>
        )
      }
    },
    {
      title: '金流綁定',
      key: 'paymentConfig',
      render: (_, r) =>
        r.hasPaymentConfig ? (
          <Tag color='green'>已綁定</Tag>
        ) : (
          <Tag>未綁定</Tag>
        )
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => {
        const terminated = record.serviceTermination?.isTerminated
        return (
          <Space wrap>
            <Button type='primary' onClick={() => openEdit(record)}>
              管理訂閱／啟用
            </Button>
            <Button onClick={() => openPaymentConfig(record)}>
              {record.hasPaymentConfig ? '更新金流金鑰' : '綁定金流'}
            </Button>
            {terminated ? (
              <Popconfirm
                title='確定要復原此店家？'
                description='將重新開放線上預約（LINE 串接不會自動還原）。'
                onConfirm={() => doReactivate(record)}
                okText='復原'
                cancelText='取消'
              >
                <Button>復原</Button>
              </Popconfirm>
            ) : (
              <Button danger onClick={() => openTerminate(record)}>
                退訂／終止服務
              </Button>
            )}
          </Space>
        )
      }
    }
  ]

  return (
    <Card>
      <Title level={2} style={{ marginBottom: 16 }}>
        使用中店家
      </Title>
      <Text type='secondary' style={{ display: 'block', marginBottom: 8 }}>
        檢視與調整店家訂閱方案、到期日與啟用狀態。
      </Text>
      <Text type='secondary' style={{ display: 'block', marginBottom: 16 }}>
        <strong>方案階層（由低到高）</strong>：免費、基礎 →{' '}
        <strong>商務方案</strong>（後端代碼 business）→{' '}
        <strong>品牌方案</strong>（brand，高於商務方案）。
        啟用店家／庫存須為商務方案或品牌方案之一。
      </Text>

      <Space wrap style={{ marginBottom: 16 }}>
        <Input
          placeholder='搜尋店名／帳號／電話'
          value={filters.search}
          onChange={(e) =>
            setFilters((f) => ({ ...f, search: e.target.value }))
          }
          style={{ width: 220 }}
          allowClear
        />
        <Select
          style={{ width: 140 }}
          value={filters.plan}
          onChange={(v) => setFilters((f) => ({ ...f, plan: v }))}
          options={[
            { value: 'all', label: '全部方案' },
            { value: 'free', label: '試用版' },
            { value: 'basic', label: '一般方案' },
            { value: 'brand', label: '高階方案' },
            { value: 'business', label: '商務方案（舊方案）' }
          ]}
        />
        <Select
          style={{ width: 140 }}
          value={filters.subscriptionStatus}
          onChange={(v) => setFilters((f) => ({ ...f, subscriptionStatus: v }))}
          options={[
            { value: 'all', label: '全部狀態' },
            { value: 'active', label: '有效' },
            { value: 'expired', label: '已過期' },
            { value: 'cancelled', label: '已取消' }
          ]}
        />
        <Select
          style={{ width: 140 }}
          value={filters.storeEnabled}
          onChange={(v) => setFilters((f) => ({ ...f, storeEnabled: v }))}
          options={[
            { value: 'all', label: '啟用狀態不限' },
            { value: 'true', label: '已啟用' },
            { value: 'false', label: '未啟用' }
          ]}
        />
        <Button type='primary' onClick={onSearch}>
          查詢
        </Button>
      </Space>

      <Table
        rowKey='_id'
        loading={loading}
        columns={columns}
        dataSource={data}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (t) => `共 ${t} 筆`
        }}
        onChange={(pag) => {
          fetchData(pag.current, pag.pageSize)
        }}
      />

      <Modal
        title={
          editing
            ? `管理：${editing.name || editing.username || editing._id}`
            : '管理店家'
        }
        open={editOpen}
        onOk={handleSave}
        onCancel={() => {
          setEditOpen(false)
          setEditing(null)
        }}
        confirmLoading={saving}
        width={560}
        destroyOnClose
      >
        <Form form={form} layout='vertical'>
          <Form.Item
            name='plan'
            label='訂閱方案'
            rules={[{ required: true, message: '請選擇方案' }]}
          >
            <Select
              options={[
                { value: 'free', label: '免費' },
                { value: 'basic', label: '基礎' },
                {
                  value: 'brand',
                  label: '高階方案（brand，可啟用店家／庫存）'
                },
                {
                  value: 'business',
                  label: '商務方案（business，已停售舊方案，請勿新指派）',
                  disabled: true
                }
              ]}
            />
          </Form.Item>
          <Form.Item
            name='status'
            label='訂閱狀態'
            rules={[{ required: true, message: '請選擇狀態' }]}
          >
            <Select
              options={[
                { value: 'active', label: '有效' },
                { value: 'expired', label: '已過期' },
                { value: 'cancelled', label: '已取消' }
              ]}
            />
          </Form.Item>
          <Form.Item name='startDate' label='開始日'>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name='endDate' label='到期日'>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name='duration' label='訂閱月數（選填）'>
            <InputNumber min={1} max={60} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name='autoRenew' label='自動續約' valuePropName='checked'>
            <Switch />
          </Form.Item>
          <Form.Item
            name='isContracted'
            label='已簽約'
            valuePropName='checked'
          >
            <Switch />
          </Form.Item>
          <Form.Item name='contractNotes' label='簽約／備註'>
            <TextArea rows={3} placeholder='內部備註' />
          </Form.Item>
          <Form.Item
            name='storeEnabled'
            label='啟用店家功能（前台營業）'
            valuePropName='checked'
            extra='商務方案與品牌方案皆可啟用；品牌方案階級高於商務方案。關閉後店家無法以營業模式使用後台。'
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* 退訂／終止服務精靈 */}
      <Modal
        title={`退訂／終止服務：${
          termTarget?.name || termTarget?.username || ''
        }`}
        open={termOpen}
        onCancel={() => {
          setTermOpen(false)
          setTermTarget(null)
          setTermResult(null)
        }}
        footer={null}
        width={640}
        destroyOnClose
      >
        <Steps
          size='small'
          current={termStep}
          style={{ marginBottom: 20 }}
          items={[{ title: '確認資訊' }, { title: '設定退訂' }, { title: '完成' }]}
        />

        {termStep === 0 && (
          <>
            <Hint
              tone='info'
              title='退訂後此店家頁面會「保留」，但生效日起停止線上預約；可選擇同步移除 LINE 串接與取消訂閱。請先確認以下資訊。'
              style={{ marginBottom: 16 }}
            />
            <Descriptions column={1} size='small' bordered>
              <Descriptions.Item label='店家名稱'>
                {termInfo?.name || termTarget?.name || '—'}
              </Descriptions.Item>
              <Descriptions.Item label='帳號'>
                {termInfo?.username || termTarget?.username || '—'}
              </Descriptions.Item>
              <Descriptions.Item label='訂閱方案／狀態'>
                {(PLAN_LABEL[termInfo?.subscription?.plan] ||
                  termInfo?.subscription?.plan ||
                  '—') +
                  '／' +
                  (STATUS_LABEL[termInfo?.subscription?.status] ||
                    termInfo?.subscription?.status ||
                    '—')}
              </Descriptions.Item>
              <Descriptions.Item label='LINE 串接'>
                {termInfo?.hasLineIntegration ? (
                  <Tag color='blue'>有串接（將移除）</Tag>
                ) : (
                  <Tag>無</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label='顧客資料筆數'>
                {termInfoLoading ? '載入中…' : `${termInfo?.customerCount ?? 0} 筆`}
              </Descriptions.Item>
            </Descriptions>

            <Divider />
            <Hint
              tone='warning'
              title='建議先匯出顧客資料再執行退訂（執行後仍可匯出）。'
              style={{ marginBottom: 12 }}
            />
            <Space>
              <Button
                onClick={() =>
                  downloadCustomers(
                    termTarget._id,
                    termInfo?.name || termTarget?.name
                  )
                }
              >
                先匯出顧客 Excel
              </Button>
              <Button type='primary' onClick={() => setTermStep(1)}>
                下一步：設定退訂
              </Button>
            </Space>
          </>
        )}

        {termStep === 1 && (
          <Form form={termForm} layout='vertical'>
            <Form.Item
              name='effectiveDate'
              label='停止預約生效日'
              extra='生效日（含）起前台停止接受新預約；可設定未來日期（例如 7/2）先行排定。'
              rules={[{ required: true, message: '請選擇生效日' }]}
            >
              <DatePicker style={{ width: '100%' }} format='YYYY-MM-DD' />
            </Form.Item>
            <Form.Item name='reason' label='退訂原因／內部備註'>
              <Input.TextArea rows={3} placeholder='內部記錄用，店家看不到' />
            </Form.Item>
            <Form.Item
              name='removeLine'
              valuePropName='checked'
              extra='清除店家 LINE 官方帳號設定、Webhook、配對請求與顧客／員工 LINE 綁定。'
            >
              <Checkbox>全面移除 LINE 串接</Checkbox>
            </Form.Item>
            <Form.Item
              name='cancelSubscription'
              valuePropName='checked'
              extra='將訂閱狀態設為「已取消」並關閉自動續約。店家頁面仍保留。'
            >
              <Checkbox>同步取消訂閱</Checkbox>
            </Form.Item>
            <Space>
              <Button onClick={() => setTermStep(0)}>上一步</Button>
              <Popconfirm
                title='確定執行退訂？'
                description='此操作會立即套用（LINE 移除為即時、停約於生效日起）。'
                onConfirm={doTerminate}
                okText='確定退訂'
                cancelText='再想想'
                okButtonProps={{ danger: true }}
              >
                <Button danger loading={termRunning}>
                  執行退訂
                </Button>
              </Popconfirm>
            </Space>
          </Form>
        )}

        {termStep === 2 && (
          <Result
            status='success'
            title='退訂處理完成'
            subTitle={
              termResult
                ? `停止預約生效日：${dayjs(termResult.effectiveDate).format(
                    'YYYY-MM-DD'
                  )}　|　訂閱狀態：${
                    STATUS_LABEL[termResult.subscriptionStatus] ||
                    termResult.subscriptionStatus
                  }　|　LINE：${termResult.lineRemoved ? '已移除' : '保留'}`
                : ''
            }
            extra={[
              <Button
                key='export'
                type='primary'
                onClick={() =>
                  downloadCustomers(
                    termTarget._id,
                    termResult?.name || termTarget?.name
                  )
                }
              >
                下載顧客資料 Excel
              </Button>,
              <Button
                key='close'
                onClick={() => {
                  setTermOpen(false)
                  setTermTarget(null)
                  setTermResult(null)
                }}
              >
                完成
              </Button>
            ]}
          />
        )}
      </Modal>
      <Modal
        title={`金流綁定：${payTarget?.name || payTarget?.username || ''}`}
        open={payOpen}
        onCancel={() => {
          setPayOpen(false)
          setPayTarget(null)
        }}
        footer={null}
        width={520}
        destroyOnClose
      >
        <p style={{ color: 'rgba(0,0,0,0.55)', marginBottom: 16 }}>
          填入該店家在藍新金流後台的商店代號與金鑰。綁定後，這間店的
          線上商店訂單與預約定金刷卡會直接進入他們自己的收單帳戶。
          金鑰會加密儲存，之後不會再以明碼顯示。
        </p>
        <Form form={payForm} layout='vertical' onFinish={submitPaymentConfig}>
          <Form.Item
            name='MerchantID'
            label='商店代號 (MerchantID)'
            rules={[{ required: true, message: '請輸入商店代號' }]}
          >
            <Input placeholder='藍新後台的商店代號' />
          </Form.Item>
          <Form.Item
            name='HashKey'
            label='HashKey'
            rules={[
              { required: true, message: '請輸入 HashKey' },
              { len: 32, message: 'HashKey 必須為 32 位' }
            ]}
          >
            <Input placeholder='32 位字串' />
          </Form.Item>
          <Form.Item
            name='HashIV'
            label='HashIV'
            rules={[
              { required: true, message: '請輸入 HashIV' },
              { len: 16, message: 'HashIV 必須為 16 位' }
            ]}
          >
            <Input placeholder='16 位字串' />
          </Form.Item>
          <Space>
            <Button type='primary' htmlType='submit' loading={paySaving}>
              儲存綁定
            </Button>
            <Button
              onClick={() => {
                setPayOpen(false)
                setPayTarget(null)
              }}
            >
              取消
            </Button>
          </Space>
        </Form>
      </Modal>
    </Card>
  )
}

export default ActiveStoresAdmin
